import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';
import {Chip, Tooltip} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faStopwatch} from '@fortawesome/pro-solid-svg-icons';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {EUserGroup} from 'apiClient/Api';
import {
    SupportSession,
    elapsedSeconds,
    formatDuration,
} from 'Pages/SupportSessions/supportSessionTypes';

/** Wie oft der Server nach einer laufenden Uhr gefragt wird. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Die laufende Zeiterfassung in der Kopfleiste.
 *
 * **Warum app-weit und nicht nur auf der Seite:** Eine laufende Uhr, die man nur dort sieht, wo man
 * sie gestartet hat, läuft irgendwann durch die Mittagspause. Hier ist sie immer im Blick, und ein
 * Klick führt zur Sitzung.
 *
 * Die Anzeige zählt selbst weiter und fragt den Server nur minütlich — die Uhr läuft schließlich
 * gleichmäßig, und ein Abruf je Sekunde wäre reine Last.
 */
export const RunningSessionChip: React.FC = () => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const navigate = useNavigate();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [session, setSession] = useState<SupportSession | null>(null);
    const [, setTick] = useState(0);

    // Zeiterfassung gibt es für die internen Rollen; für alle anderen gar nicht erst fragen.
    const isInternal = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.OFFICE;

    useEffect(() => {
        if (!isInternal) return;

        let active = true;

        const poll = (): void => {
            // Still: hängt die Anbindung oder fehlt der Endpunkt, bleibt die Kopfleiste leer —
            // ein Fehler in einer Nebenanzeige darf die Anwendung nicht stören.
            http.get<SupportSession | null>('/SupportSessions/Running')
                .then((r) => { if (active) setSession(r.data ?? null); })
                .catch(() => { if (active) setSession(null); });
        };

        poll();
        const timer = window.setInterval(poll, POLL_INTERVAL_MS);

        return () => {
            active = false;
            window.clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInternal]);

    // Zwischen den Abrufen selbst weiterzählen, damit die Anzeige nicht stehenbleibt.
    useEffect(() => {
        if (session === null) return;
        const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
        return () => window.clearInterval(timer);
    }, [session]);

    if (session === null) return null;

    return (
        <Tooltip title={session.subject?.trim() || t('supportSessions.running')}>
            <Chip
                color="success"
                size="small"
                icon={<FontAwesomeIcon icon={faStopwatch} style={{marginLeft: 8}}/>}
                label={formatDuration(elapsedSeconds(session))}
                onClick={() => navigate('/zeiterfassung')}
                sx={{mr: 1, fontVariantNumeric: 'tabular-nums', cursor: 'pointer'}}
            />
        </Tooltip>
    );
};

export default RunningSessionChip;
